import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Instagram, MapPin, Phone, RefreshCw, Sprout } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import Logo from "@/components/Logo";
import { MaskLine, Reveal, spotlight } from "@/components/Reveal";
import { useCategories, useProducts } from "@/lib/queries";
import { CATEGORY_FALLBACK, HERO_IMAGE, PROMO_IMAGE, scrollToId } from "@/lib/api";

const MARQUEE_ITEMS = [
    "OPEN 24 HOURS · MON–SUN",
    "10 FLORIDA AVE NW",
    "WASHINGTON, DC",
    "FREE GIFT WITH EVERY ART PURCHASE",
    "FLOWER · EDIBLES · VAPES · CONCENTRATES",
    "(202) 713-4325",
];

function Hero() {
    return (
        <section className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="hero-section">
            <div className="absolute inset-0 -z-10">
                <motion.div
                    initial={{ scale: 1.12 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full w-full"
                >
                    <img src={HERO_IMAGE} alt="" className="h-full w-full object-cover opacity-40" />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/75 to-ink/40" />
                <div
                    className="absolute inset-0"
                    style={{ background: "radial-gradient(58% 50% at 72% 28%, rgba(76,175,80,0.2), transparent 70%)" }}
                />
            </div>

            <div className="container-x relative grid w-full gap-10 pb-16 pt-32 md:pb-24 lg:grid-cols-12 lg:items-center lg:pt-24">
                <div className="lg:col-span-7">
                    <MaskLine delay={0.15}>
                        <span className="eyebrow text-gold">Washington DC — Dispensary & Art Gallery</span>
                    </MaskLine>
                    <h1 className="mt-5 font-display font-extrabold leading-[0.94] tracking-tight text-bone text-[clamp(2.5rem,6.6vw,5.6rem)]">
                        <MaskLine delay={0.28}>THE DISTRICT'S</MaskLine>
                        <MaskLine delay={0.42}>
                            <span className="text-outline">GREENEST</span>
                        </MaskLine>
                        <MaskLine delay={0.56}>
                            MENU.{" "}
                            <em className="font-serif italic font-medium text-leaf-bright text-[0.62em] align-middle">
                                always open.
                            </em>
                        </MaskLine>
                    </h1>
                    <MaskLine delay={0.72}>
                        <p className="mt-6 max-w-xl text-base md:text-lg text-bone/75 leading-relaxed">
                            Premium flower, edibles, vapes and concentrates from a shop that doubles as an art gallery —
                            with a free cannabis gift alongside every art purchase.
                        </p>
                    </MaskLine>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/shop"
                            data-testid="hero-shop-cta"
                            className="group inline-flex items-center gap-3 rounded-full bg-leaf px-8 py-4 font-bold text-ink transition-all hover:bg-leaf-bright hover:gap-5"
                        >
                            Shop the Menu
                            <ArrowRight size={18} className="transition-transform" />
                        </Link>
                        <Link
                            to="/deals"
                            data-testid="hero-deals-cta"
                            className="inline-flex items-center gap-3 rounded-full border border-bone/25 px-8 py-4 font-semibold text-bone transition-colors hover:border-leaf/60 hover:text-leaf-bright"
                        >
                            View Deals
                        </Link>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.1, duration: 0.8 }}
                        className="mt-10 flex flex-wrap gap-x-7 gap-y-3 font-mono text-[11px] uppercase tracking-[0.18em] text-sage"
                    >
                        <span className="flex items-center gap-2"><MapPin size={13} className="text-leaf" /> 10 Florida Ave NW</span>
                        <span className="flex items-center gap-2"><Clock size={13} className="text-leaf" /> Open 24 Hours, Mon–Sun</span>
                        <a href="tel:+12027134325" data-testid="hero-phone-link" className="flex items-center gap-2 hover:text-leaf-bright">
                            <Phone size={13} className="text-leaf" /> (202) 713-4325
                        </a>
                    </motion.div>
                </div>

                <div className="hidden lg:col-span-5 lg:block">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="relative"
                    >
                        <div className="arch overflow-hidden border border-leaf/25 shadow-[0_40px_120px_-30px_rgba(76,175,80,0.45)]">
                            <img
                                src="https://images.unsplash.com/photo-1634449277989-0e50ad2ca43e?crop=entropy&cs=srgb&fm=jpg&q=85"
                                alt="Featured exotic cannabis product at District Greenz"
                                className="aspect-[4/5] w-full object-cover"
                            />
                        </div>
                        <div className="absolute -left-8 top-1/3 animate-float rounded-2xl border border-gold/40 bg-ink/80 px-5 py-4 backdrop-blur-xl">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">The Gifting Bar</p>
                            <p className="mt-1 font-display text-sm font-bold text-bone">Free gift with every art purchase</p>
                        </div>
                    </motion.div>
                </div>
            </div>

            <motion.button
                onClick={() => scrollToId("categories")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-sage hover:text-leaf-bright transition-colors"
                data-testid="hero-scroll-cue"
                aria-label="Scroll to categories"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                <span className="h-8 w-px bg-gradient-to-b from-leaf to-transparent" />
            </motion.button>
        </section>
    );
}

function Categories() {
    const { data, isLoading, isError, refetch } = useCategories();

    return (
        <section id="categories" className="py-20 md:py-28" data-testid="categories-section">
            <div className="container-x">
                <Reveal className="flex items-end justify-between gap-6">
                    <div>
                        <p className="eyebrow text-leaf-bright">Shop by Category</p>
                        <h2 className="mt-3 font-display text-3xl md:text-5xl font-extrabold text-bone">
                            Find your lane.
                        </h2>
                    </div>
                    <Link to="/shop" data-testid="categories-view-all" className="hidden md:inline-flex items-center gap-2 eyebrow text-bone/70 hover:text-leaf-bright transition-colors">
                        View all <ArrowRight size={14} />
                    </Link>
                </Reveal>

                {isLoading ? (
                    <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <div key={i} className="skeleton aspect-[4/5] rounded-2xl" />
                        ))}
                    </div>
                ) : isError ? (
                    <CatalogWarming onRetry={refetch} />
                ) : (
                    <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                        {data.categories.slice(0, 10).map((cat, i) => (
                            <Reveal key={cat.id} delay={(i % 5) * 0.07}>
                                <Link
                                    to={`/shop?category=${cat.slug}`}
                                    data-testid={`category-card-${cat.slug}`}
                                    className="group relative block aspect-[4/5] overflow-hidden rounded-2xl border border-leaf/12"
                                >
                                    {cat.image || CATEGORY_FALLBACK[cat.slug] ? (
                                        <img
                                            src={cat.image || CATEGORY_FALLBACK[cat.slug]}
                                            alt={`${cat.name} products`}
                                            loading="lazy"
                                            className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-700 group-hover:scale-105 group-hover:opacity-80"
                                        />
                                    ) : (
                                        <div className="absolute inset-0 flex items-center justify-center bg-moss text-leaf/40">
                                            <Sprout size={32} />
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
                                    <div className="absolute inset-x-0 bottom-0 p-4">
                                        <h3 className="font-display text-base md:text-lg font-bold text-bone leading-tight">
                                            {cat.name}
                                        </h3>
                                        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-sage">
                                            {cat.count} {cat.count === 1 ? "product" : "products"}
                                        </p>
                                    </div>
                                    <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-bone/25 text-bone opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:border-leaf group-hover:text-leaf-bright">
                                        <ArrowRight size={13} />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

export function CatalogWarming({ onRetry }) {
    return (
        <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-leaf/15 bg-moss/40 px-8 py-16 text-center" data-testid="catalog-warming">
            <RefreshCw size={26} className="text-leaf" />
            <p className="font-display text-xl font-bold text-bone">The menu is warming up</p>
            <p className="max-w-sm text-sm text-sage">
                We're syncing live inventory from the shop right now. Give it a moment and try again.
            </p>
            {onRetry && (
                <button
                    data-testid="catalog-retry-button"
                    onClick={onRetry}
                    className="mt-2 rounded-full border border-leaf/40 px-6 py-3 text-sm font-semibold text-leaf-bright hover:bg-leaf/10 transition-colors"
                >
                    Retry
                </button>
            )}
        </div>
    );
}

function Featured() {
    const { data, isLoading, isError, refetch } = useProducts({ orderby: "popularity", per_page: 8 });

    return (
        <section className="bg-bone py-20 md:py-28 text-ink" data-testid="featured-section">
            <div className="container-x">
                <Reveal className="flex items-end justify-between gap-6">
                    <div>
                        <p className="eyebrow text-leaf-deep">Fresh on the Menu</p>
                        <h2 className="mt-3 font-display text-3xl md:text-5xl font-extrabold">Most wanted.</h2>
                    </div>
                    <Link to="/shop" data-testid="featured-shop-all" className="hidden md:inline-flex items-center gap-2 eyebrow text-ink/60 hover:text-leaf-deep transition-colors">
                        Shop all <ArrowRight size={14} />
                    </Link>
                </Reveal>

                {isLoading ? (
                    <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="skeleton aspect-[4/5] rounded-2xl" />
                        ))}
                    </div>
                ) : isError ? (
                    <div className="mt-10 text-ink">
                        <CatalogWarming onRetry={refetch} />
                    </div>
                ) : (
                    <div className="mt-10 flex gap-4 overflow-x-auto no-scrollbar snap-x pb-2 md:grid md:grid-cols-4">
                        {data.products.map((p) => (
                            <div key={p.id} className="w-[240px] shrink-0 snap-start md:w-auto">
                                <ProductCard product={p} light />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function Deals() {
    const sales = useProducts({ on_sale: true, per_page: 48 });
    const dealsCat = useProducts({ category: "deal-discount", per_page: 48 });
    const loading = sales.isLoading || dealsCat.isLoading;
    const isError = sales.isError && dealsCat.isError;

    const merged = useMemo(() => {
        const seen = new Set();
        const out = [];
        for (const p of [...(dealsCat.data?.products || []), ...(sales.data?.products || [])]) {
            if (!seen.has(p.id)) {
                seen.add(p.id);
                out.push(p);
            }
        }
        return out;
    }, [sales.data, dealsCat.data]);

    if (isError || (!loading && merged.length === 0)) return null;

    return (
        <section className="py-20 md:py-28" data-testid="deals-section">
            <div className="container-x">
                <Reveal className="flex items-end justify-between gap-6">
                    <div>
                        <p className="eyebrow text-gold">Deals & Steals</p>
                        <h2 className="mt-3 font-display text-3xl md:text-5xl font-extrabold text-bone">
                            Every deal, one place.
                        </h2>
                    </div>
                    <Link to="/deals" data-testid="deals-view-all" className="hidden md:inline-flex items-center gap-2 eyebrow text-bone/70 hover:text-gold transition-colors">
                        All deals <ArrowRight size={14} />
                    </Link>
                </Reveal>
                {loading ? (
                    <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="skeleton aspect-[4/5] rounded-2xl" />
                        ))}
                    </div>
                ) : (
                    <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="deals-grid">
                        {merged.map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function ArtPromo() {
    return (
        <section className="py-20 md:py-28" data-testid="art-promo-section">
            <div className="container-x grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                <Reveal>
                    <div className="relative overflow-hidden rounded-3xl border border-leaf/15">
                        <img src={PROMO_IMAGE} alt="Art pieces at District Greenz DMV" loading="lazy" className="aspect-[4/3] w-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-tr from-ink/60 to-transparent" />
                    </div>
                </Reveal>
                <Reveal delay={0.12}>
                    <p className="eyebrow text-gold">The Art Initiative</p>
                    <h2 className="mt-4 font-serif text-4xl md:text-5xl italic leading-tight text-bone">
                        "Art for the people, green for the District."
                    </h2>
                    <p className="mt-6 max-w-lg text-bone/70 leading-relaxed">
                        District Greenz doubles as an art gallery in the heart of Penn Quarter. Every art purchase comes
                        with a free cannabis gift — our way of supporting DC's creative community.
                    </p>
                    <Link
                        to="/shop"
                        data-testid="art-promo-cta"
                        className="mt-8 inline-flex items-center gap-3 rounded-full border border-leaf/40 px-8 py-4 font-semibold text-leaf-bright transition-colors hover:bg-leaf/10"
                    >
                        Browse the Menu <ArrowRight size={17} />
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

function CatalogShelf({ id, testid, eyebrow, title, params }) {
    const { data, isLoading, isError, refetch } = useProducts(params);
    if (isError || (!isLoading && !data?.products?.length)) return null;
    return (
        <section id={id} className="py-20 md:py-28" data-testid={testid}>
            <div className="container-x">
                <Reveal className="flex items-end justify-between gap-6">
                    <div>
                        <p className="eyebrow text-leaf-bright">{eyebrow}</p>
                        <h2 className="mt-3 font-display text-3xl md:text-5xl font-extrabold text-bone">{title}</h2>
                    </div>
                    <Link
                        to={`/shop?category=${params.category}`}
                        data-testid={`${testid}-view-all`}
                        className="hidden md:inline-flex items-center gap-2 eyebrow text-bone/70 hover:text-leaf-bright transition-colors"
                    >
                        View all <ArrowRight size={14} />
                    </Link>
                </Reveal>
                {isLoading ? (
                    <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="skeleton aspect-[4/5] rounded-2xl" />
                        ))}
                    </div>
                ) : (
                    <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid={`${testid}-grid`}>
                        {data.products.slice(0, 8).map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function PrerollsShelf() {
    return (
        <CatalogShelf
            id="prerolls"
            testid="prerolls-section"
            eyebrow="Hand-rolled & ready"
            title="Prerolls."
            params={{ category: "prerolls", per_page: 8 }}
        />
    );
}

function ShakesShelf() {
    return (
        <CatalogShelf
            id="shakes"
            testid="shakes-section"
            eyebrow="Value picks"
            title="Shake & smalls."
            params={{ category: "shake", per_page: 8 }}
        />
    );
}

const STEPS = [
    { n: "01", title: "Browse the live menu", body: "Real-time stock from the shop — flower, edibles, vapes, concentrates and more." },
    { n: "02", title: "Reserve your picks", body: "Add items to your bag, apply a coupon, and choose local delivery or store pickup." },
    { n: "03", title: "Show ID, get your green", body: "Pay with cash on delivery or at the shop. A valid 21+ government ID is required at hand-off." },
];

function HowItWorks() {
    return (
        <section id="delivery" className="border-y border-leaf/12 bg-moss/30 py-20 md:py-28" data-testid="delivery-section">
            <div className="container-x">
                <Reveal>
                    <p className="eyebrow text-leaf-bright">How it works</p>
                    <h2 className="mt-3 font-display text-3xl md:text-5xl font-extrabold text-bone">
                        Delivered or picked up. Your call.
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-5 md:grid-cols-3">
                    {STEPS.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.1}>
                            <div className="h-full rounded-3xl border border-leaf/12 bg-ink/50 p-8 card-spotlight" onMouseMove={spotlight}>
                                <span className="font-display text-5xl font-extrabold text-outline">{s.n}</span>
                                <h3 className="mt-6 font-display text-xl font-bold text-bone">{s.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-sage">{s.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

const FAQS = [
    { q: "Where are you located?", a: "10 Florida Ave NW, Washington, DC 20001 — walk-ins welcome 24 hours, every day." },
    { q: "What are your hours?", a: "We're open 24 hours a day, Monday through Sunday — every hour of every week." },
    { q: "How does delivery work?", a: "Place your order online and we'll bring it to your door across Washington DC, including the 20007, 20011 and 20009 zip codes. You pay with cash when it arrives. A valid 21+ government-issued ID is required at hand-off." },
    { q: "Can I pick up my order instead?", a: "Yes — choose store pickup at checkout, then pay at the shop when you collect your order. Same 21+ ID requirement applies." },
    { q: "What's the deal with free gifts?", a: "Every art purchase comes with a free cannabis gift. It's our way of blending the gallery with the greenz." },
];

function Faq() {
    const [open, setOpen] = useState(0);
    return (
        <section id="faq" className="py-20 md:py-28" data-testid="faq-section">
            <div className="container-x grid gap-12 lg:grid-cols-12">
                <Reveal className="lg:col-span-5">
                    <p className="eyebrow text-leaf-bright">FAQ</p>
                    <h2 className="mt-3 font-display text-3xl md:text-5xl font-extrabold text-bone">
                        Questions, answered.
                    </h2>
                    <p className="mt-5 max-w-sm text-sm leading-relaxed text-sage">
                        Still curious? Call the shop any time — literally, we're open 24 hours a day.
                    </p>
                </Reveal>
                <div className="lg:col-span-7 space-y-3">
                    {FAQS.map((f, i) => (
                        <Reveal key={f.q} delay={i * 0.05}>
                            <div className="rounded-2xl border border-leaf/12 bg-moss/40" data-testid="faq-item">
                                <button
                                    data-testid={`faq-toggle-${i}`}
                                    onClick={() => setOpen(open === i ? -1 : i)}
                                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                                >
                                    <span className="font-display font-bold text-bone">{f.q}</span>
                                    <span className={`text-leaf-bright transition-transform duration-300 ${open === i ? "rotate-45" : ""}`}>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        </svg>
                                    </span>
                                </button>
                                <motion.div
                                    initial={false}
                                    animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
                                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                    className="overflow-hidden"
                                >
                                    <p className="px-6 pb-6 text-sm leading-relaxed text-bone/70">{f.a}</p>
                                </motion.div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Contact() {
    return (
        <section id="contact" className="py-20 md:py-28" data-testid="contact-section">
            <div className="container-x">
                <Reveal>
                    <div className="relative overflow-hidden rounded-3xl border border-leaf/20 bg-moss/40 px-8 py-14 md:px-16 md:py-20 text-center">
                        <div
                            className="absolute inset-0"
                            style={{ background: "radial-gradient(50% 60% at 50% 100%, rgba(76,175,80,0.15), transparent 70%)" }}
                        />
                        <div className="relative">
                            <Logo className="mx-auto h-12" alt="" />
                            <h2 className="mt-8 font-serif text-4xl md:text-6xl italic text-bone">Come through.</h2>
                            <p className="mt-5 text-bone/70">
                                Washington DC — Delivering to 20007, 20011 & 20009 · Open 24 Hours, Monday–Sunday
                            </p>
                            <div className="mt-9 flex flex-wrap justify-center gap-4">
                                <a
                                    href="tel:+12027134325"
                                    data-testid="contact-call-cta"
                                    className="rounded-full bg-leaf px-8 py-4 font-bold text-ink hover:bg-leaf-bright transition-colors"
                                >
                                    Call (202) 713-4325
                                </a>
                                <a
                                    href="https://www.instagram.com/majestic_greenz"
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    data-testid="contact-instagram-cta"
                                    className="rounded-full border border-leaf/40 px-8 py-4 font-semibold text-leaf-bright hover:bg-leaf/10 transition-colors inline-flex items-center gap-2.5"
                                >
                                    <Instagram size={17} /> @majestic_greenz
                                </a>
                                <a
                                    href="https://maps.google.com/?q=10+Florida+Ave+NW+Washington+DC+20001"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="contact-directions-cta"
                                    className="rounded-full border border-bone/25 px-8 py-4 font-semibold text-bone hover:border-leaf/50 transition-colors"
                                >
                                    Pickup at 10 Florida Ave NW
                                </a>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <>
            <Seo
                title="District Greenz DMV — Cannabis Dispensary & Delivery in Washington, DC"
                description="Shop premium flower, edibles, vapes and concentrates from District Greenz DMV. Delivering in Washington DC 20007, 20011, 20009 — open 24 hours, Mon–Sun."
                path="/"
            />
            <Hero />
            <Marquee items={MARQUEE_ITEMS} />
            <Categories />
            <Featured />
            <PrerollsShelf />
            <ShakesShelf />
            <Deals />
            <ArtPromo />
            <HowItWorks />
            <Faq />
            <Contact />
        </>
    );
}
